import React, { useState, useEffect, useCallback } from "react";
import EventCard from "../../components/EventCard";
import { getFeaturedEvents } from "../../crud/event-listing.crud";
import ShimmerGrid from "../../revampComponents/ShimmerGrid";
import { debounce } from "lodash";
import Pagination from "./Pagination";
import MobilePagination from "./MobilePagination";
const EventList = () => {
  const [eventData, setEventData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [show, setShow] = useState(false);
  const limit = 9;
  useEffect(() => {
    getEventsFunction();
  }, []);
  const getEventsFunction = async () => {
    setIsLoading(true);
    const res = await getFeaturedEvents();
    if (res.status === 200) {
      if (Array.isArray(res.data?.featuredEvents)) {
        const updatedData = res.data?.featuredEvents.map(({
          name,
          ...rest
        }) => ({
          ...rest,
          event_name: name
        }));
        setEventData(updatedData);
        setFilteredData(updatedData); // Initially, all data is displayed
      }
      setIsLoading(false);
    }
  };
  const handleSearch = query => {
    setIsSearching(false); // Hide shimmer grid once search is complete
    if (query === "") {
      setFilteredData(eventData); // Reset to all events if search query is empty
    } else {
      const filteredEvents = eventData.filter(event => event.event_name.toLowerCase().includes(query.toLowerCase()));
      setFilteredData(filteredEvents);
    }
    setCurrentPage(1); // Reset to first page on new search
  };
  const debouncedSearch = useCallback(debounce(value => {
    handleSearch(value);
  }, 300), [eventData]);
  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchQuery(value); // Update search query immediately
    if (value.length !== 0) {
      setShow(true);
      setIsSearching(true); // Show shimmer grid when typing starts
      debouncedSearch(value); // Perform the debounced search
    } else {
      handleClearSearch(); // Clear search if input is empty
    }
  };
  const handleClearSearch = () => {
    setSearchQuery("");
    setFilteredData(eventData); // Reset to show all events
    setShow(false); // Hide the cross icon
    setIsSearching(false); // Stop shimmer effect
  };

  // Scroll to top of the event list on page change
  const scrollToTop = () => {
    window.scrollTo({
      top: 500,
      behavior: "smooth"
    });
  };

  // Pagination handlers
  const handlePageClick = pageNumber => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      scrollToTop();
    }, 200);
  };
  const handleNextClick = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setTimeout(() => {
        scrollToTop();
      }, 200);
    }
  };
  const handlePreviousClick = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setTimeout(() => {
        scrollToTop();
      }, 200);
    }
  };
  const totalPages = Math.ceil(filteredData.length / limit);

  // Paginated events to display on the current page
  const paginatedEvents = filteredData.slice((currentPage - 1) * limit, currentPage * limit);
  return <div className="px-sm-5 px-3 py-5 font-figtree" data-sentry-component="EventList" data-sentry-source-file="EventList.jsx">
			<div className="featured-events-container mb-4">
				<h6 className="featured-events-title">FEATURED EVENTS</h6>
				<div className="search-bar d-flex  justify-space-between align-items-center">
					<div className="d-flex justify-space-between gap-2">
						<span className="search-icon">
							<img src="https://d2nnrpx7gk2my2.cloudfront.net/files/6706c2be839faa0008e452fb/searchmagnifyingglass.svg" alt="search icon" />
						</span>
						<input type="text" placeholder="Search Events" value={searchQuery} onChange={handleSearchChange} />
					</div>
					{show && <img alt="clear search" onClick={handleClearSearch} // Add onClick to clear search
        style={{
          cursor: "pointer"
        }} className="ml-auto mr-0" src="https://d2nnrpx7gk2my2.cloudfront.net/files/6720c9d97b8c7b0008a8cae3/Cancel.svg" />}
				</div>
			</div>
			<div className="row d-flex">
				{isLoading || isSearching ? <ShimmerGrid count={12} /> : paginatedEvents.length === 0 ? <div style={{
        minHeight: "100px"
      }} className="no-resource-found text-center mt-5">
						No events found
					</div> : paginatedEvents.map((event, index) => <EventCard key={index} event={event} />)}
			</div>

			{filteredData.length > 0 && totalPages > 1 && <div>
					<Pagination currentPage={currentPage} totalPages={totalPages} onPageClick={handlePageClick} onNextClick={handleNextClick} onPreviousClick={handlePreviousClick} />
					<MobilePagination totalPages={totalPages} currentPage={currentPage} onNextClick={handleNextClick} onPreviousClick={handlePreviousClick} />
				</div>}
			<style>
				{`
          .featured-events-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }
          .featured-events-title {
            font-size: 16px;
            font-weight: 600;
            color: #525252;
          }
          .search-bar {
            display: flex;
            border: 1px solid #D6D6D6;
            padding: 5px 30px 5px 10px;
            border-radius: 2px;
            gap: 5px;
            max-width: 350px;
            width: 100%;
            justify-content:space-between;
          }
          .search-bar input {
            border: none;
            outline: none;
            font-size: 16px;
            font-weight: 400;
            color: #525252;
          }
          .search-bar .search-icon {
            margin-left: 5px;
          }
          @media (max-width: 576px) {
            .featured-events-container {
              flex-direction: column;
              align-items: start;
              gap: 10px;
            }
            .search-bar {
              width: 100%;
              padding-top: 11px;
              padding-bottom: 11px;
            }
          }
        `}
			</style>
		</div>;
};
export default EventList;